import { useMemo } from 'react'

import { yupResolver } from '@hookform/resolvers/yup'
import { useCommercialStructure } from 'commons/customHooks'
import { t } from 'commons/libs/i18n'
import { dateAdd, formatDate, getToday } from 'commons/utils/date'
import { Badge, DatePicker, Divider, Flex, Modal, Select, Text } from 'components'
import { Controller, useForm } from 'react-hook-form'
import type { SalesCalendarListItem } from 'services/salesCalendar/interfaces'
import {
  createSalesCalendar,
  editSalesCalendar,
} from 'store/SalesCalendarContext/salesCalendarSlice'
import { useDispatch } from 'store/store'

import { DATE_FORMAT, ISO_FORMAT } from './constants'
import * as S from './styles'
import { getCalendarValidationSchema } from './validations'

export enum CalendarCreationModalAction {
  CREATE = 'CREATE',
  EDIT = 'EDIT',
}

export interface CalendarCreationModalDefaultValues {
  approvalBeginDate: string | null
  approvalEndDate: string | null
  fairShareMonth: string | null
  havaBeginDate: string | null
  havaEndDate: string | null
  officeCode: string
  portalBeginDate: string | null
  portalEndDate: string | null
}

export type CalendarCreationModalProps = {
  action: CalendarCreationModalAction
  open: boolean
  setOpen: (open: boolean) => void
  defaultOffice?: string
  defaultShareMonth?: number
  disableYearMonth?: boolean
  calendarData?: SalesCalendarListItem
  onCreationSuccess?: () => void
}

export const CalendarCreationModal = ({
  action,
  open,
  setOpen,
  defaultOffice,
  defaultShareMonth,
  disableYearMonth,
  calendarData,
  onCreationSuccess,
}: CalendarCreationModalProps) => {
  const dispatch = useDispatch()
  const { getOffices } = useCommercialStructure()

  const isEdit = action === CalendarCreationModalAction.EDIT

  const officeOptions = getOffices().map((office) => ({
    title: `${office.id} - ${office.description}`,
    value: office.id,
  }))

  const parseNumericYearMonth = (value: number) => {
    return formatDate(String(value), 'YYYY-MM-DDTHH:mm:ss', 'YYYYMM')
  }

  const defaultValues = useMemo(() => {
    if (isEdit && calendarData) {
      return {
        approvalBeginDate: calendarData.initialDateApproval || null,
        approvalEndDate: calendarData.endDateApproval || null,
        fairShareMonth: parseNumericYearMonth(calendarData.yearMonth) || null,
        havaBeginDate: calendarData.initialDateHava || null,
        havaEndDate: calendarData.endDateHava || null,
        officeCode: calendarData.officeCode || '',
        portalBeginDate: calendarData.initialDatePortal || null,
        portalEndDate: calendarData.endDatePortal || null,
      }
    }

    return {
      approvalBeginDate: null,
      approvalEndDate: null,
      fairShareMonth: defaultShareMonth ? parseNumericYearMonth(defaultShareMonth) : null,
      havaBeginDate: null,
      havaEndDate: null,
      officeCode: defaultOffice || '',
      portalBeginDate: null,
      portalEndDate: null,
    }
  }, [isEdit, calendarData, defaultOffice])

  const schema = useMemo(
    () => getCalendarValidationSchema(isEdit, defaultValues),
    [isEdit, defaultValues],
  )

  const {
    control,
    handleSubmit,
    trigger,
    formState: { isValid, isDirty },
  } = useForm({
    defaultValues,
    mode: 'onChange',
    resolver: yupResolver(schema),
  })

  const handleSaveCalendar = handleSubmit((data) => {
    if (isEdit && calendarData) {
      const params = {
        officeCode: calendarData.officeCode,
        yearMonth: calendarData.yearMonth,
      }
      const editPayload = {
        approvalBeginDate: data.approvalBeginDate,
        approvalEndDate: data.approvalEndDate,
        havaBeginDate: data.havaBeginDate,
        havaEndDate: data.havaEndDate,
        portalBeginDate: data.portalBeginDate,
        portalEndDate: data.portalEndDate,
      }
      dispatch(editSalesCalendar(params, editPayload))
    } else {
      const payload = {
        ...data,
        fairShareMonth: Number(formatDate(data.fairShareMonth, 'YYYYMM')),
      }
      dispatch(createSalesCalendar(payload, onCreationSuccess))
    }
    setOpen(false)
  })

  const handleClose = () => {
    setOpen(false)
  }

  const getBadge = () => {
    if (isValid && isDirty) {
      return (
        <Badge appearance="soft" color="success">
          {isEdit ? t('common.messages.readyToSave') : t('common.messages.readyToConfirm')}
        </Badge>
      )
    }
    return (
      <Badge appearance="soft" color="neutral">
        {isEdit ? t('common.messages.noChangesDone') : t('common.messages.fillFieldsToConfirm')}
      </Badge>
    )
  }

  return (
    <Modal
      actionBadge={getBadge()}
      buttonAction={handleSaveCalendar}
      buttonDisabled={!isValid || !isDirty}
      buttonText={isEdit ? t('buttons.save') : t('buttons.confirm')}
      closeAction={handleClose}
      modalWidth={550}
      open={open}
      returnAction={handleClose}
      setOpen={setOpen}
      showAction
      showClose
      showReturn
      title={
        isEdit ? t('screens.salesCalendar.editPeriod') : t('screens.salesCalendar.addNewPeriod')
      }
      titleSize={18}
    >
      <Flex direction="column" fullWidth gap="md">
        <Flex alignItems="flex-end" fullWidth gap="sm">
          <S.FieldWrapper>
            <Controller
              control={control}
              name="officeCode"
              render={({ field: { name, value, onChange }, fieldState }) => (
                <Select
                  disabled={isEdit}
                  error={fieldState.error?.message}
                  id="officeCode"
                  label={t('common.office')}
                  menuText={t('common.placeholders.select')}
                  name={name}
                  onChange={(e) => onChange(e)}
                  options={officeOptions}
                  value={value}
                />
              )}
            />
          </S.FieldWrapper>
          <S.FieldWrapper>
            <Controller
              control={control}
              name="fairShareMonth"
              render={({ field, fieldState }) => (
                <DatePicker
                  {...field}
                  disabled={isEdit || disableYearMonth}
                  disableMaskedInput={false}
                  errorState={!!fieldState.error?.message}
                  height={30}
                  inputFormat="MM/YYYY"
                  label={t('screens.salesCalendar.fairShare')}
                  maxDate={dateAdd(getToday(), 3, 'years', DATE_FORMAT, ISO_FORMAT)}
                  minDate={getToday()}
                  onChange={(value) => field.onChange(formatDate(value, DATE_FORMAT))}
                  placeholder="mm/aaaa"
                  views={['year', 'month']}
                />
              )}
            />
          </S.FieldWrapper>
        </Flex>

        <Divider />

        <Flex alignItems="flex-end" fullWidth gap="sm">
          <S.FieldWrapper>
            <Controller
              control={control}
              name="portalBeginDate"
              render={({ field, fieldState }) => (
                <DatePicker
                  {...field}
                  errorState={!!fieldState.error?.message}
                  height={30}
                  label={t('screens.salesCalendar.portalWindow')}
                  minDate={getToday()}
                  onChange={(value) => {
                    field.onChange(formatDate(value, DATE_FORMAT))
                    trigger('portalEndDate')
                  }}
                  placeholder={t('common.placeholders.select')}
                />
              )}
            />
          </S.FieldWrapper>
          <S.TextWrapper>
            <Text size="2xs" weight="md">
              {t('common.until')}
            </Text>
          </S.TextWrapper>
          <S.FieldWrapper>
            <Controller
              control={control}
              name="portalEndDate"
              render={({ field, fieldState }) => (
                <DatePicker
                  {...field}
                  errorState={!!fieldState.error?.message}
                  height={30}
                  minDate={getToday()}
                  onChange={(value) => {
                    field.onChange(formatDate(value, DATE_FORMAT))
                    trigger('portalBeginDate')
                  }}
                  placeholder={t('common.placeholders.select')}
                />
              )}
            />
          </S.FieldWrapper>
        </Flex>

        <Flex alignItems="flex-end" fullWidth gap="sm">
          <S.FieldWrapper>
            <Controller
              control={control}
              name="approvalBeginDate"
              render={({ field, fieldState }) => (
                <DatePicker
                  {...field}
                  errorState={!!fieldState.error?.message}
                  height={30}
                  label={t('screens.salesCalendar.approvalND')}
                  minDate={getToday()}
                  onChange={(value) => {
                    field.onChange(formatDate(value, DATE_FORMAT))
                    trigger('approvalEndDate')
                  }}
                  placeholder={t('common.placeholders.select')}
                />
              )}
            />
          </S.FieldWrapper>
          <S.TextWrapper>
            <Text size="2xs" weight="md">
              {t('common.until')}
            </Text>
          </S.TextWrapper>
          <S.FieldWrapper>
            <Controller
              control={control}
              name="approvalEndDate"
              render={({ field, fieldState }) => (
                <DatePicker
                  {...field}
                  errorState={!!fieldState.error?.message}
                  height={30}
                  minDate={getToday()}
                  onChange={(value) => {
                    field.onChange(formatDate(value, DATE_FORMAT))
                    trigger('approvalBeginDate')
                  }}
                  placeholder={t('common.placeholders.select')}
                />
              )}
            />
          </S.FieldWrapper>
        </Flex>

        <Flex alignItems="flex-end" fullWidth gap="sm">
          <S.FieldWrapper>
            <Controller
              control={control}
              name="havaBeginDate"
              render={({ field, fieldState }) => (
                <DatePicker
                  {...field}
                  errorState={!!fieldState.error?.message}
                  height={30}
                  label={t('screens.salesCalendar.havaWindow')}
                  minDate={getToday()}
                  onChange={(value) => {
                    field.onChange(formatDate(value, DATE_FORMAT))
                    trigger('havaEndDate')
                  }}
                  placeholder={t('common.placeholders.select')}
                />
              )}
            />
          </S.FieldWrapper>
          <S.TextWrapper>
            <Text size="2xs" weight="md">
              {t('common.until')}
            </Text>
          </S.TextWrapper>
          <S.FieldWrapper>
            <Controller
              control={control}
              name="havaEndDate"
              render={({ field, fieldState }) => (
                <DatePicker
                  {...field}
                  errorState={!!fieldState.error?.message}
                  height={30}
                  minDate={getToday()}
                  onChange={(value) => {
                    field.onChange(formatDate(value, DATE_FORMAT))
                    trigger('havaBeginDate')
                  }}
                  placeholder={t('common.placeholders.select')}
                />
              )}
            />
          </S.FieldWrapper>
        </Flex>
      </Flex>
    </Modal>
  )
}
